import usePanelState from '@databases/panel/usePanelState';
import { useCallback } from 'react';

import { MAX_POPOVER_EDIT_WIDTH } from './popover-constants';

export interface AdjustTextAreaDimensionsProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  maxWidthReachedRef: React.MutableRefObject<boolean>;
  maxPopoverEditWidth?: number;
}

export const useAdjustTextAreaDimensions = ({
  textAreaRef,
  maxWidthReachedRef,
  maxPopoverEditWidth = MAX_POPOVER_EDIT_WIDTH,
}: AdjustTextAreaDimensionsProps) => {
  const { isPanelVisible } = usePanelState();
  const isAnyPanelVisible = isPanelVisible();
  const whiteSpace = isAnyPanelVisible ? 'normal' : 'nowrap';

  return useCallback(() => {
    const textArea = textAreaRef.current;

    if (!textArea) {
      return;
    }

    const computedStyle = window.getComputedStyle(textArea);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const paddingTotal = paddingTop + paddingBottom;
    // `scrollHeight` includes padding calculation, which needs to be removed
    textArea.style.height = `${textArea.scrollHeight - paddingTotal}px`;
    textArea.style.minHeight = '100%';
    textArea.style.minWidth = 'auto';
    textArea.style.whiteSpace = whiteSpace;

    // Keeps characters from hitting the edge of the text area
    const textAreaWidthBuffer = 15;
    const parentWidth = textArea.parentElement?.clientWidth || 0;
    let width = Math.min(textArea.scrollWidth, maxPopoverEditWidth);
    if (textArea.scrollWidth >= parentWidth) {
      // Content is touching edge, add buffer
      width = Math.min(textArea.scrollWidth + textAreaWidthBuffer, maxPopoverEditWidth);
    }

    textArea.style.minWidth = `${width}px`;

    if (width >= maxPopoverEditWidth && !maxWidthReachedRef.current) {
      maxWidthReachedRef.current = true;
    } else if (width < maxPopoverEditWidth && maxWidthReachedRef.current) {
      maxWidthReachedRef.current = false;
    }

    // when we text wrap, max width is already reached, so the text area does
    // not need to expand any further
    if (maxWidthReachedRef.current || whiteSpace === 'normal') {
      const middleOfScreen = globalThis.innerHeight / 2;
      const topOfTextArea = textArea.getBoundingClientRect().top;
      const textAreaContainerHeight = textArea.parentElement?.clientHeight || 0;
      textArea.style.whiteSpace = 'normal';
      // Text area height will retain cell height at a minimum and expand vertically
      textArea.style.height = `${Math.max(
        textAreaContainerHeight,
        textArea.scrollHeight
      )}px`;

      // Text area opens "downwards" if above middle of screen and "upwards" if below
      if (topOfTextArea > middleOfScreen) {
        textArea.style.bottom = '0px';
      } else {
        textArea.style.top = '0px';
      }
    }
  }, [maxPopoverEditWidth, maxWidthReachedRef, textAreaRef, whiteSpace]);
};
